import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';

import {
  ClientResourceContainer,
  createClientResourceContainer,
  LegacyClientResourceContainerQuoteStructure,
  Role,
  ROLE,
  useClient,
} from '@/features/Clients';
import { mapToClientResource } from '@/features/Clients/ClientMapper/clientMapper';
import { useGlobalStore } from '@/features/Global/store';
import { legacyInventoryContractTransformers } from '@/features/Quotes/transformers/transformers';
import type {
  LegacyClientMapData,
  LegacyInventoryContractCallbackData,
  LegacyQuoteContent,
  QuoteContent,
} from '@/features/Quotes/types';
import { isValidQuoteContent, QuoteVersion } from '@/features/Quotes/types';
import {
  useContractStore,
  useInputStore,
  useInputStoreSchema,
} from '@/features/Sparten';
import { createDefaultSortingState } from '@/features/Tariffs/sorting';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { getDifference, getNextDay } from '@/utils/dates/dates';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import type { KeepDataFromQuoteMapping } from '@/features/Sparten/types';
import type { SearchDataComparable } from '@/features/Global/types';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { usePausableWatchers } from '@/utils/usePausableWatchers/usePauseableWatchers';
import { mergeWithDeep } from '@/utils/objects/objects';

export const transformInventoryContractToCurrentVersion = (
  contractData: Record<string, unknown>,
): Record<string, unknown> => {
  const transformers =
    legacyInventoryContractTransformers[useGlobalStore().getSparte];
  const state: Record<string, unknown> = contractData;

  /*
   * If a given path has a transformator, provide legacy data to the transformator.
   * This way we can transform data into a new structure. Example: pfeInventoryContractMapping in pfe.ts
   */
  if (transformers !== undefined) {
    Object.entries(transformers).forEach(([path, transformator]) => {
      if (transformator) {
        state[path] = transformator(
          contractData[path] as LegacyInventoryContractCallbackData,
        );
      }
    });
  }

  return state;
};

export const transformLegacyQuoteToCurrentVersion = (
  legacyContent: LegacyQuoteContent,
): QuoteContent => {
  let content: Partial<QuoteContent> = {};

  content.searchData = {
    eingabedaten: legacyContent.suche?.eingabedaten ?? {},
    entities: legacyContent.suche?.entities ?? {},
    merkmale: legacyContent.suche?.merkmale ?? [],
    blacklistedCompaniesHash: '',
  };

  content.filters = {
    directAgreement: legacyContent.filters?.directAgreement ?? false,
    search: [], // not supported
    sorting: createDefaultSortingState(),
  };

  content.comparison = {
    selection: legacyContent.vergleich?.vergleichGroup ?? [],
    recommendation: legacyContent.vergleich?.empfehlung ?? null,
  };

  // Holds clients ids with aggregated roles
  const clientLoadMap: {
    [key: number]: LegacyClientMapData;
  } = {};

  // Holds clients without ids
  const clientWithoutIdLoadArray: LegacyClientMapData[] = [];

  Object.entries(legacyContent.kunden).forEach(([role, data]) => {
    const clientRole: ROLE = role as ROLE;

    if (data.id === undefined) {
      clientWithoutIdLoadArray.push({
        roles: [clientRole],
        data: mapToClientResource(data, clientRole),
      });

      return;
    }

    // Map client id with roles to avoid multiple loading of same client
    const map = clientLoadMap[data.id] ?? {
      roles: [],
      data: mapToClientResource(data, clientRole),
    };
    map.roles.push(clientRole);
    clientLoadMap[data.id] = map;
  });

  const mappedClientsData: ClientResourceContainer[] = [];
  // Add clients which don't have an ID
  clientWithoutIdLoadArray.forEach((client) => {
    mappedClientsData.push(
      createClientResourceContainer(client.roles, client.data),
    );
  });

  // Add clients which have an ID
  Object.entries(clientLoadMap).forEach(([, client]) => {
    mappedClientsData.push(
      createClientResourceContainer(client.roles, client.data),
    );
  });

  content.clients = mappedClientsData;

  content.version = QuoteVersion;

  const spartenConfig = getCurrentSpartenConfig();

  if (spartenConfig.quoteTransformationCallback !== undefined) {
    content = {
      ...content,
      ...spartenConfig.quoteTransformationCallback(legacyContent),
    };
  }

  if (isValidQuoteContent(content)) {
    return content;
  }

  throw new VglError(VGL_ERROR_CODE.QUOTE_TRANSFORMATION_FAILED, {
    additionalErrorMessage: JSON.stringify(legacyContent),
  });
};

export const transferContractDataToContractStore = (
  quoteContractData: Record<string, unknown>,
): void => {
  const contractStore = useContractStore();
  const defaultState = cloneDeep(contractStore.$state);
  const contractData = useStorableState().read(quoteContractData);

  const mergedState = mergeWithDeep(defaultState, contractData);

  contractStore.$patch(mergedState as Partial<typeof contractData.$state>);
};

export const buildMergedInputData = (
  quoteInputData: Record<string, unknown>,
  shouldKeepVersicherungsbeginn = false,
): Record<string, unknown> => {
  const inputSchema = useInputStoreSchema();
  const config = getCurrentSpartenConfig();

  const eingabedaten = useStorableState().read(quoteInputData);

  const nextDay = getNextDay();
  const begin = eingabedaten.versicherungsbeginn as Date;

  if (
    getDifference(begin, nextDay) < 0 &&
    !shouldKeepVersicherungsbeginn &&
    config.isVersicherungsbeginnFromThePastAllowed !== true
  ) {
    eingabedaten.versicherungsbeginn = nextDay;
  }

  const currentState = inputSchema.getDefault() as unknown;

  return mergeWithDeep(currentState, eingabedaten) as Record<string, unknown>;
};

const validateInputData = (_data: Record<string, unknown>): void => {
  // const inputSchema = useInputStoreSchema();
  //
  // try {
  //   inputSchema.validateSync(data, {
  //     abortEarly: false,
  //   });
  // } catch (error) {
  //   if (error instanceof ValidationError) {
  //     throw new VglError(VGL_ERROR_CODE.QUOTE_INVALID_INPUT_DATA, {
  //       cause: error,
  //       additionalData: {
  //         inputData: data,
  //         validationResult: parseInnerErrors(error),
  //       },
  //     });
  //   } else {
  //     throw new VglError(VGL_ERROR_CODE.QUOTE_INVALID_INPUT_DATA, {
  //       cause: error,
  //       additionalData: {
  //         inputData: data,
  //       },
  //     });
  //   }
  // }
};

export const transferQuoteDataToInputStore = (
  quoteInputData: Record<string, unknown>,
  shouldKeepVersicherungsbeginn = false,
): void => {
  const inputStore = useInputStore();

  let mergedInputData = buildMergedInputData(
    quoteInputData,
    shouldKeepVersicherungsbeginn,
  );
  const config = getCurrentSpartenConfig();

  if (
    config.quotePreInputMergeCallback !== undefined &&
    useGlobalStore().isWebreport === false
  ) {
    mergedInputData = config.quotePreInputMergeCallback(mergedInputData);
    validateInputData(config.quotePreInputMergeCallback(quoteInputData));
  } else {
    validateInputData(quoteInputData);
  }

  usePausableWatchers().pauseWhile(() => {
    inputStore.$patch(mergedInputData as Partial<typeof inputStore.$state>);
  });
};

export const transferQuoteAllTimeTariffsDataToInputStore = (
  data: Record<string, unknown>,
): void => {
  const config = getCurrentSpartenConfig();
  if (config.quoteLoadAllTimeTariffCallback !== undefined) {
    config.quoteLoadAllTimeTariffCallback(data);
  }
};

export const applyQuoteDataToExistingEntity = (
  loadedEntity: ClientResourceContainer,
  role: Role,
  mappings: KeepDataFromQuoteMapping[],
): void => {
  const config = getCurrentSpartenConfig();
  if (config.keepDataFromQuote !== undefined) {
    const { client } = useClient(role);
    const clientProxy = client.value.data;

    if (clientProxy !== undefined) {
      mappings.forEach((mapping) => {
        const quoteValue = get(loadedEntity.data, mapping.path);

        if (
          mapping.skipIfQuoteValueIsOneOf !== undefined &&
          mapping.skipIfQuoteValueIsOneOf.includes(quoteValue)
        ) {
          return;
        }

        set(clientProxy, mapping.path, mapping.formatter(quoteValue));
      });
    }
  }
};

export const removeNullAndUndefinedValues = (obj: SearchDataComparable) => {
  return JSON.parse(
    JSON.stringify(obj, (_key, value) => {
      return value === null ? undefined : value;
    }),
  );
};

export const transformClientEntitiesToEntityManagerStructure = (
  entities: LegacyClientResourceContainerQuoteStructure[],
): ClientResourceContainer[] => {
  const transformedEntities: ClientResourceContainer[] = [];

  entities.forEach((entity) => {
    transformedEntities.push({
      refId: entity._refId,
      type: ENTITY_TYPE.CLIENT,
      isDirty: entity.dirty,
      data: entity.data,
      isLoading: entity.isLoading,
      roles: entity.roles,
    });
  });

  return transformedEntities;
};
